import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  useTheme,
  useMediaQuery,
  Fade,
  Chip
} from '@mui/material';
import {
  ArrowForward,
  Visibility,
  Star,
  ArrowBackIosNew as ArrowBackIos,
  ArrowForwardIos
} from '@mui/icons-material';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { useLanguage } from '../../../localization';
import tokens from '../../../theme/tokens';
import { professionToPersonaId } from '../../../personas';

// Import diverse template collection
import MonochromeClassicCenteredTimelineTemplate from '../../../templates/definitions/ClassicCenteredTimeline/MonochromeClassicCenteredTimeline';
import BlueSidebarLeftModernTemplate from '../../../templates/definitions/SidebarLeftModern/BlueSidebarLeftModernTemplate';
import NavyClassicCenteredTimelineTemplate from '../../../templates/definitions/ClassicCenteredTimeline/NavyClassicCenteredTimeline';
import GreenGeometricTemplate from '../../../templates/definitions/Geometric/GreenGeometricTemplate';
import MonochromeSidebarLeftTemplate from '../../../templates/definitions/SidebarLeftClassic/MonochromeSidebarLeftTemplate';
import TealClassicCenteredTimelineTemplate from '../../../templates/definitions/ClassicCenteredTimeline/TealClassicCenteredTimeline';
import BlueClassicCenteredTimelineTemplate from '../../../templates/definitions/ClassicCenteredTimeline/BlueClassicCenteredTimeline';
import BlueTriangularTemplate from '../../../templates/definitions/Triangular/BlueTriangularTemplate';
import BlueSidebarRightModernTemplate from '../../../templates/definitions/SidebarRightModern/BlueSidebarRightModernTemplate';
import YellowGeometricTemplate from '../../../templates/definitions/Geometric/YellowGeometricTemplate';
import TealSidebarLeftTemplate from '../../../templates/definitions/SidebarLeftClassic/TealSidebarLeftTemplate';
import GreenSidebarLeftModernLightTemplate from '../../../templates/definitions/SidebarLeftModern/GreenSidebarLeftModernLightTemplate';
import NavySidebarRightTemplate from '../../../templates/definitions/SidebarRightClassic/NavySidebarRightTemplate';
import TealSidebarRightModernLightTemplate from '../../../templates/definitions/SidebarRightModern/TealSidebarRightModernLightTemplate';
import GreenClassicCenteredTimelineTemplate from '../../../templates/definitions/ClassicCenteredTimeline/GreenClassicCenteredTimeline';
import OrangeTriangularTemplate from '../../../templates/definitions/Triangular/OrangeTriangularTemplate';
import YellowSidebarLeftModernLightTemplate from '../../../templates/definitions/SidebarLeftModern/YellowSidebarLeftModernLightTemplate';
import MonochromeGeometricTemplate from '../../../templates/definitions/Geometric/MonochromeGeometricTemplate';
import MonochromeSidebarRightModernTemplate from '../../../templates/definitions/SidebarRightModern/MonochromeSidebarRightModernTemplate';
import BurgundyTriangularTemplate from '../../../templates/definitions/Triangular/BurgundyTriangularTemplate';
import BlueTopbarTemplate from '../../../templates/definitions/TopbarClassic/BlueTopbarTemplate';
import GreenTopbarModernTemplate from '../../../templates/definitions/TopbarModern/GreenTopbarModernTemplate';
import NavyTopbarModernLightTemplate from '../../../templates/definitions/TopbarModern/NavyTopbarModernLightTemplate';
import MonochromeTopbarTemplate from '../../../templates/definitions/TopbarClassic/MonochromeTopbarTemplate';
import OrangeTopbarModernTemplate from '../../../templates/definitions/TopbarModern/OrangeTopbarModernTemplate';

const Templates = ({ onGetStarted }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useLanguage();

  const templates = [
    {
      ...BlueSidebarLeftModernTemplate,
      name: 'Software Engineer',
      description: t('templates.descriptions.softwareEngineer'),
      popular: true,
    },
    {
      ...BlueTopbarTemplate,
      name: 'Account Executive',
      description: t('templates.descriptions.accountExecutive'),
      popular: false,
    },
    {
      ...MonochromeClassicCenteredTimelineTemplate,
      name: 'Healthcare Professional',
      description: t('templates.descriptions.healthcareProfessional'),
      popular: true,
    },
    {
      ...GreenGeometricTemplate,
      name: 'Marketing Specialist',
      description: t('templates.descriptions.marketingSpecialist'),
      popular: false,
    },
    {
      ...GreenTopbarModernTemplate,
      name: 'Environmental Scientist',
      description: t('templates.descriptions.environmentalScientist'),
      popular: false,
    },
    {
      ...NavyClassicCenteredTimelineTemplate,
      name: 'Financial Advisor',
      description: t('templates.descriptions.financialAdvisor'),
      popular: true,
    },
    {
      ...BlueTriangularTemplate,
      name: 'Civil Engineer',
      description: t('templates.descriptions.civilEngineer'),
      popular: false,
    },
    {
      ...NavyTopbarModernLightTemplate,
      name: 'Management Consultant',
      description: t('templates.descriptions.managementConsultant'),
      popular: true,
    },
    {
      ...MonochromeSidebarLeftTemplate,
      name: 'Legal Counsel',
      description: t('templates.descriptions.legalCounsel'),
      popular: false,
    },
    {
      ...YellowGeometricTemplate,
      name: 'Creative Director',
      description: t('templates.descriptions.creativeDirector'),
      popular: false,
    },
    {
      ...TealClassicCenteredTimelineTemplate,
      name: 'Education Leader',
      description: t('templates.descriptions.educationLeader'),
      popular: false,
    },
    {
      ...BlueSidebarRightModernTemplate,
      name: 'Sales Executive',
      description: t('templates.descriptions.salesExecutive'),
      popular: true,
    },
    {
      ...MonochromeTopbarTemplate,
      name: 'Corporate Executive',
      description: t('templates.descriptions.corporateExecutive'),
      popular: true,
    },
    {
      ...GreenSidebarLeftModernLightTemplate,
      name: 'HR Specialist',
      description: t('templates.descriptions.hrSpecialist'),
      popular: false,
    },
    {
      ...BlueClassicCenteredTimelineTemplate,
      name: 'Business Consultant',
      description: t('templates.descriptions.businessConsultant'),
      popular: true,
    },
    {
      ...OrangeTopbarModernTemplate,
      name: 'Product Manager',
      description: t('templates.descriptions.productManager'),
      popular: false,
    },
    {
      ...TealSidebarLeftTemplate,
      name: 'Operations Manager',
      description: t('templates.descriptions.operationsManager'),
      popular: false,
    },
    {
      ...NavySidebarRightTemplate,
      name: 'Real Estate Agent',
      description: t('templates.descriptions.realEstateAgent'),
      popular: false,
    },
    {
      ...OrangeTriangularTemplate,
      name: 'Startup Founder',
      description: t('templates.descriptions.startupFounder'),
      popular: true,
    },
    {
      ...TealSidebarRightModernLightTemplate,
      name: 'Hospitality Manager',
      description: t('templates.descriptions.hospitalityManager'),
      popular: false,
    },
    {
      ...MonochromeGeometricTemplate,
      name: 'Research Scientist',
      description: t('templates.descriptions.researchScientist'),
      popular: false,
    },
    {
      ...YellowSidebarLeftModernLightTemplate,
      name: 'Retail Manager',
      description: t('templates.descriptions.retailManager'),
      popular: false,
    },
    {
      ...GreenClassicCenteredTimelineTemplate,
      name: 'Nonprofit Director',
      description: t('templates.descriptions.nonprofitDirector'),
      popular: false,
    },
    {
      ...MonochromeSidebarRightModernTemplate,
      name: 'Project Manager',
      description: t('templates.descriptions.projectManager'),
      popular: true,
    },
    {
      ...BurgundyTriangularTemplate,
      name: 'Executive Leader',
      description: t('templates.descriptions.executiveLeader'),
      popular: true,
    }
  ];

  const handleTemplateSelect = (template) => {
    // Use URL parameter approach - this will override any existing localStorage
    console.log('Landing page: Selected template:', template.id);
    console.log('Landing page: Selected profession:', template.name);
    
    const templateParam = encodeURIComponent(template.id);
    const personaParam = professionToPersonaId(template.name);
    
    // Build URL with both template and persona parameters
    const appUrl = `/app?template=${templateParam}&persona=${personaParam}`;
    console.log('Landing page: Navigating to:', appUrl);
    
    // Use window.location.href to ensure a fresh page load and trigger the useEffect in App.js
    window.location.href = appUrl;
  };

  const CustomArrow = (props) => {
    const { className, style, onClick, direction, icon } = props;
    // Hide arrows on small mobile, as dots are sufficient and space is limited
    if (isSmallMobile) {
      return null;
    }
    return (
      <Box
        className={className}
        sx={{
          ...style,
          display: 'block',
          color: tokens.colors.neutral[500],
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          borderRadius: '50%',
          width: 40, 
          height: 40,
          zIndex: 2,
          '&:hover': {
            color: tokens.colors.primary.main,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
          },
          '&::before': {
            display: 'none' // Remove default slick arrow styling
          },
          // Custom positioning
          position: 'absolute',
          top: '50%',
          transform: 'translateY(-50%)',
          ...(direction === 'prev' ? { left: '-20px' } : { right: '-20px' }),
          // Hide on medium and down if not explicitly styled for them
          [theme.breakpoints.down('md')]: {
             // Example: Keep them visible but smaller or adjust position
             // For now, let's ensure they don't overlap badly on medium screens
             // Or simply hide them if they cause issues on tablets:
             // display: 'none' 
             // Adjusting for tablet - might need further tweaking based on actual layout
             ...(direction === 'prev' ? { left: '-5px' } : { right: '-5px' }),
          },
           [theme.breakpoints.down('sm')]: {
            display: 'none', // Explicitly hide on small screens (sm and xs)
          }
        }}
        onClick={onClick}
      >
        {icon}
      </Box>
    );
  };

  const TemplatePreview = ({ template }) => (
    <Box
      sx={{
        width: '100%',
        height: 320, // Increased height for better aspect ratio
        backgroundColor: 'white',
        borderRadius: tokens.radius.lg,
        border: `1px solid ${tokens.colors.borders.light}`,
        overflow: 'hidden',
        position: 'relative',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'scale(1.02)',
          boxShadow: tokens.shadows.lg
        }
      }}
      onClick={() => handleTemplateSelect(template)}
    >
      {/* Use actual template thumbnail */}
      <Box
        component="img"
        src={template.thumbnail}
        alt={`${template.name} template preview`}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'contain', // Changed from 'cover' to 'contain' to prevent stretching
          objectPosition: 'center', // Center the image
          backgroundColor: '#f5f5f5', // Light background to fill any gaps
        }}
      />

      {/* Popular badge */}
      {template.popular && (
        <Chip
          icon={<Star sx={{ fontSize: 16 }} />}
          label={t('templates.popular')}
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: tokens.colors.warning,
            color: 'white',
            fontWeight: tokens.typography.fontWeights.semibold,
            '& .MuiChip-icon': {
              color: 'white'
            }
          }}
        />
      )}
    </Box>
  );

  const sliderSettings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    nextArrow: <CustomArrow direction="next" icon={<ArrowForwardIos sx={{ fontSize: '1.2rem', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />} />,
    prevArrow: <CustomArrow direction="prev" icon={<ArrowBackIos sx={{ fontSize: '1.2rem', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />} />,
    responsive: [
      {
        breakpoint: 1024, // md
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          infinite: true,
          dots: true,
          arrows: !isMobile // Show arrows only if not mobile (md and up)
        }
      },
      {
        breakpoint: 600, // sm
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false, // Ensure arrows are off for small mobile
          dots: false // Remove dots on mobile devices to prevent additional rows
        }
      }
    ]
  };

  return (
    <Box
      id="templates"
      sx={{
        py: { xs: 5, md: 12 },
        backgroundColor: 'white',
        position: 'relative',
        // Add some padding on the sides for the arrows on larger screens
        px: { xs: 0, md: '40px' } 
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 8 } }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '1.6rem', md: '2.5rem', lg: '3rem' },
              fontWeight: tokens.typography.fontWeights.bold,
              color: tokens.colors.text.primary,
              mb: { xs: 1, md: 2 }
            }}
          >
            {t('templates.title')}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: '0.9rem', md: '1.1rem' },
              color: tokens.colors.text.secondary,
              maxWidth: '600px',
              mx: 'auto',
              lineHeight: 1.6,
              mb: { xs: 3, md: 4 }
            }}
          >
            {t('templates.subtitle')}
          </Typography>
          
          {/* Stats */}
          <Box sx={{ 
            display: { xs: 'none', sm: 'flex' }, 
            justifyContent: 'center', 
            gap: 4, 
            flexWrap: 'wrap' 
          }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: tokens.typography.fontWeights.bold,
                  color: tokens.colors.primary.main,
                  fontSize: '2rem'
                }}
              >
                80+
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: tokens.colors.text.secondary }}
              >
                {t('templates.stats.templates')}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: tokens.typography.fontWeights.bold,
                  color: '#9C27B0', // Beautiful purple for infinite customization
                  fontSize: '2rem'
                }}
              >
                ∞
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: tokens.colors.text.secondary }}
              >
                {t('templates.stats.customization')}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: tokens.typography.fontWeights.bold,
                  color: tokens.colors.success,
                  fontSize: '2rem'
                }}
              >
                25+
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: tokens.colors.text.secondary }}
              >
                {t('templates.stats.industries')}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: tokens.typography.fontWeights.bold,
                  color: tokens.colors.secondary.main,
                  fontSize: '2rem'
                }}
              >
                100%
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: tokens.colors.text.secondary }}
              >
                {t('templates.stats.atsCompatible')}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Templates Carousel */}
        <Box sx={{
          mb: { xs: 5, md: 8 }, 
          '.slick-slide > div': { margin: '0 10px' }, 
          '.slick-list': { margin: '0 -10px'},
          '.slick-slide': { height: { xs: '480px', md: '580px' } }, // Responsive height
          '.slick-track': { display: 'flex', alignItems: 'stretch' }
          }}>
          <Slider {...sliderSettings}>
            {templates.map((template) => (
              <Box key={template.id} sx={{ p: 1, height: { xs: '480px', md: '580px' } }}> {/* Responsive height */}
                <Card
                  sx={{
                    height: '100%',
                    backgroundColor: 'white',
                    border: `1px solid ${tokens.colors.borders.light}`,
                    borderRadius: tokens.radius.xl,
                    boxShadow: tokens.shadows.sm,
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      boxShadow: tokens.shadows.lg,
                      transform: 'translateY(-4px)',
                      borderColor: tokens.colors.primary.main
                    }
                  }}
                  onClick={() => handleTemplateSelect(template)}
                >
                  <Box sx={{ p: 2 }}>
                    <TemplatePreview template={template} />
                  </Box>
                  
                  <CardContent sx={{ pt: 0, pb: 3, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 0 }}> {/* Made CardContent grow and manage space with minHeight for text truncation */}
                    <Box> {/* Wrapper for top part of card content */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: tokens.typography.fontWeights.semibold,
                            color: tokens.colors.text.primary,
                            fontSize: '1.1rem'
                          }}
                        >
                          {template.name}
                        </Typography>
                        <Chip
                          label={template.category}
                          size="small"
                          sx={{
                            backgroundColor: tokens.colors.neutral[100],
                            color: tokens.colors.text.secondary,
                            fontSize: '0.75rem'
                          }}
                        />
                      </Box>
                      
                      <Typography
                        variant="body2"
                        sx={{
                          color: tokens.colors.text.secondary,
                          lineHeight: 1.5,
                          mb: 2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical'
                        }}
                      >
                        {template.description}
                      </Typography>
                    </Box>

                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Visibility />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTemplateSelect(template);
                      }}
                      sx={{
                        color: tokens.colors.primary.main,
                        borderColor: tokens.colors.primary.main,
                        textTransform: 'none',
                        fontWeight: tokens.typography.fontWeights.medium,
                        alignSelf: 'flex-start', // Align button to the start of the flex container
                        display: { xs: 'none', md: 'inline-flex' }, // Hide on mobile devices
                        '&:hover': {
                          backgroundColor: tokens.colors.primary.main,
                          color: 'white'
                        }
                      }}
                    >
                      {t('templates.useTemplate')}
                    </Button>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Slider>
        </Box>

        {/* Bottom CTA */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: tokens.typography.fontWeights.semibold,
              color: tokens.colors.text.primary,
              mb: 2,
              fontSize: { xs: '1.3rem', md: '1.5rem' }
            }}
          >
            {t('templates.finalCta.title')}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: tokens.colors.text.secondary,
              mb: 4,
              fontSize: '1.1rem'
            }}
          >
            {t('templates.finalCta.subtitle')}
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={onGetStarted}
            endIcon={<ArrowForward />}
            sx={{
              backgroundColor: tokens.colors.primary.main,
              color: 'white',
              fontWeight: tokens.typography.fontWeights.semibold,
              textTransform: 'none',
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              borderRadius: tokens.radius.base,
              boxShadow: tokens.shadows.lg,
              '&:hover': {
                backgroundColor: tokens.colors.primary.dark,
                boxShadow: tokens.shadows.elegant,
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {t('templates.finalCta.button')}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default Templates; 